import { AuthenticationTest, Address } from "../types";
import { DateTime } from "luxon";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  DEFAULT_CURRENCY,
  TIMEZONE_ABBR_MAP,
  TIMEZONE_MAP,
} from "../constants";

export const formatAddress = ({
  country,
  postalCode,
  addressLine1,
  addressLine2,
  addressLine3,
}: Address) => {
  let address = "";

  address += addressLine1 || "";
  address += `${address ? ", " : ""}${addressLine2 || ""}`;
  address += `${address ? ", " : ""}${addressLine3 || ""}`;
  address += `${address ? ", " : ""}${postalCode || ""}`;
  address += `${address ? ", " : ""}${country || ""}`;

  return address || "Not Given";
};

export const getListItemTypeForDocumentAuth = (
  authenticationTest: AuthenticationTest
) => {
  if (authenticationTest.result === "ATTENTION") {
    return "warning";
  }

  if (authenticationTest.result === "FAILED") {
    return "danger";
  }
  return "success";
};

export const getListItemTypeForIdv = (result: string) => {
  if (result.includes("PARTIAL_MATCH")) {
    return "warning";
  }

  if (result.includes("NOT_MATCH")) {
    return "danger";
  }
  return "success";
};

export const formatDate = (
  date: string,
  format = "yyyy-MM-dd",
  timezone?: string
) => {
  return timezone
    ? DateTime.fromISO(date).setZone(timezone).toFormat(format)
    : DateTime.fromISO(date).toFormat(format);
};

/**
 * Get timezone based on app country code
 * @param appCountry - The app country code (e.g., "SG", "HK")
 * @returns The timezone string (e.g., "Asia/Singapore", "Asia/Hong_Kong")
 */
export const getTimezoneByCountry = (appCountry: string): string => {
  const country = appCountry.toUpperCase();
  return TIMEZONE_MAP[country] || TIMEZONE_MAP.SG;
};

/**
 * Get timezone abbreviation based on app country code
 * @param appCountry - The app country code (e.g., "SG", "HK")
 * @returns The timezone abbreviation (e.g., "SGT", "HKT")
 */
export const getTimezoneAbbreviation = (appCountry: string): string => {
  const country = appCountry.toUpperCase();
  return TIMEZONE_ABBR_MAP[country] || TIMEZONE_ABBR_MAP.SG;
};

/**
 * Format date with timezone based on app country
 * @param date - The date string to format
 * @param format - The format string (default: "d MMMM yyyy h:mma")
 * @param appCountry - The app country code (e.g., "SG", "HK")
 * @returns Formatted date string with timezone abbreviation
 */
export const formatDateByCountry = (
  date: string,
  format: string = "d MMMM yyyy h:mma",
  appCountry: string = "SG"
): string => {
  const timezone = getTimezoneByCountry(appCountry);
  const timezoneAbbr = getTimezoneAbbreviation(appCountry);
  return `${formatDate(date, format, timezone)} ${timezoneAbbr}`;
};

export const isAeUser = (user: {
  app_country?: string;
  appCountry?: string;
}): boolean => {
  const country = (user.app_country ?? user.appCountry ?? "")
    .trim()
    .toUpperCase();
  return country === "AE" || country === "UAE";
};

export const getCurrencySymbol = (currencyCode = DEFAULT_CURRENCY) => {
  return getSymbolFromCurrency(currencyCode)!;
};

export const formatDecimalNumber = (input: number) => {
  const [naturalPart, decimalPart] = input.toString().split(".");
  const formattedNaturalPart = Number(naturalPart).toLocaleString();
  if (!!decimalPart) return [formattedNaturalPart, decimalPart].join(".");
  return formattedNaturalPart;
};
